import "./_style.less";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBookingAssessment } from "../../providers/booking-assessment.provider";
import { VuiButton, VuiContentLoading, VuiFormCard, VuiSecondaryButton } from "../../../../@vendor/components";
import { Divider, Row, Space } from "antd";
import { Col } from "antd";
import { AssessmentIcon } from "../../../../components";
import { useAppSelector } from "../../../../stores/hooks";
import { useCartBookingAssessment } from "../booking-assessment/hooks/use-cart-booking-assessment";
import ModalScheduleDetails from "./components/modal-schedule-details";
import { useCheckoutBookingAssessment } from "../../providers/checkout-booking-assessment.provider";

const BookingAssessmentStepPreview = () => {
  const { t } = useTranslation();
  const { account } = useAppSelector((state) => state.system);
  const { currentStep, form } = useBookingAssessment();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const { values } = form;
  const { assessmentSchedules = [] } = values;
  const sessionCount = values?.assessment?.number_of_sessions;
  const isSingleSession = sessionCount === 1;

  const {
    handleBookingCart,
    cartLoading,
  } = useCartBookingAssessment({
    formValues: values,
    assessmentSchedules,
  });

  const isLogin = !!account?.id;

  useEffect(() => {
    if (currentStep === 2) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep]);

  if (currentStep !== 2) {
    return null;
  }

  let dateTimeLabel = "";
  if (
    isSingleSession &&
    values?.schedule?.[0] &&
    values?.time?.[0]
  ) {
    const dateObj = new Date(values.schedule[0].date);
    const formattedDate = dateObj.toLocaleDateString(undefined, {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    dateTimeLabel = `${formattedDate} | ${values.time[0].label} (GMT +7)`;
  }

  const price = values?.assessment?.price ?? 0;
  const priceLabel = `Rp${price.toLocaleString("id-ID")}`;

  const sessionDetails = isSingleSession ? [] : (values?.schedule || []).map((schedule: any, idx: number) => {
    const dateObj = schedule ? new Date(schedule.date) : null;
    const formattedDate = dateObj ? dateObj.toLocaleDateString(undefined, {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    }) : "";
    let timeLabel = "";
    if (Array.isArray(values?.time) && values.time[idx]) {
      timeLabel = values.time[idx].label;
    }
    return {
      session: idx + 1,
      formattedDate,
      rawDate: schedule?.date || "",
      timeLabel,
      label: `${formattedDate}${timeLabel ? ' | ' + timeLabel + ' (GMT +7)' : ''}`
    };
  });

  const handleCloseScheduleModal = () => setShowScheduleModal(false);

  return (
    <>
      <VuiFormCard className="preview-booking-card">
        <Row gutter={[40, 0]} align="top" justify="space-between">
          <Col xs={24} md={8} className="preview-left-col">
            <div className="avatar-wrapper">
              <img
                className="avatar-image preview-avatar-image"
                src={values?.assessment?.photo?.url}
                alt={values?.assessment?.photo?.url}
              />
            </div>
            <div className="preview-package-info">
              <label className="preview-package-label">{t("common.text.package")}</label>
              <div className="preview-package-name">{values?.assessment?.name}</div>
            </div>
          </Col>

          <Col xs={24} md={16} className="preview-right-col">
            <Row gutter={[20, 20]}>
              <Col span={24}>
                <div className="preview-section-title">
                  <AssessmentIcon color="#FFA64C" style={{ height: 24, width: 24 }} />
                  {t("common.text.assesmentAndPrograms")}
                </div>
              </Col>
              {values?.psychologist?.name && (
                <Col xs={24} md={12}>
                  <div className="preview-label">Psychologist</div>
                  <div className="preview-value">{values.psychologist.name}</div>
                </Col>
              )}
              {values?.type && (
                <Col xs={24} md={12}>
                  <div className="preview-label">Assessment & Programs Method</div>
                  <div className="preview-value">{values?.type}</div>
                </Col>
              )}
              {values?.assessment?.has_schedule && (
                <Col xs={24} md={12}>
                  <div className="preview-label">Date & Time</div>
                  {isSingleSession ? (
                    <div className="preview-value">{dateTimeLabel}</div>
                  ) : (
                    <button
                      className="preview-schedule-details-btn"
                      onClick={() => setShowScheduleModal(true)}
                    >
                      View Schedule Details
                    </button>
                  )}
                </Col>
              )}
              <Col xs={24} md={12}>
                <div className="preview-label">Patient</div>
                <div className="preview-value">{values?.patient?.name || "-"}</div>
              </Col>
              <Col xs={24}>
                <div className="preview-price-row">
                  <Col xs={24} md={8}>
                    <Space direction="vertical" size={0}>
                      <div className="preview-price-label">Total</div>
                      <div className="preview-price">{priceLabel}</div>
                      <div className="preview-price-note">*Price above is exclude tax</div>
                    </Space>
                  </Col>

                  <Col xs={24} md={16} className="preview-action-btns">
                    {isLogin ? (
                      <>
                        <VuiButton
                          loading={cartLoading}
                          label={t("common.button.addToCart")}
                          buttonProps={{
                            htmlType: "button",
                            type: "default",
                            onClick: () => handleBookingCart("cart"),
                          }}
                        />
                        <VuiButton
                          loading={cartLoading}
                          label={t("common.button.directCheckout")}
                          buttonProps={{
                            htmlType: "submit",
                            type: "primary",
                            onClick: () => handleBookingCart("checkout"),
                          }}
                        />
                      </>
                    ) : (
                      <VuiButton
                        loading={cartLoading}
                        label={t("common.button.checkout")}
                        buttonProps={{
                          htmlType: "submit",
                          type: "primary",
                          onClick: () => handleBookingCart("guest"),
                        }}
                      />
                    )}
                  </Col>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </VuiFormCard>
      <ModalScheduleDetails
        visible={showScheduleModal}
        onCancel={handleCloseScheduleModal}
        sessionDetails={sessionDetails}
      />
    </>
  );
};

export default BookingAssessmentStepPreview;